import { useState } from 'react';
import { EmailTextSelect, Pill, formatDkk, projectTypeLabel, type PillVariant } from '@jywer/ui';
import { deleteRate, patchRate, triggerRateNow } from './api';
import { TriggerTaskPicker } from './TriggerTaskPicker';
import { RATE_STATUSES, STATUS_LABEL, type RateRow as Row, type RateStatus } from './types';

const STATUS_VARIANT: Record<RateStatus, PillVariant> = {
  pending: 'neutral',
  triggered: 'info',
  invoice_created: 'info',
  invoiced: 'warning',
  paid: 'success',
  cancelled: 'muted',
};

export function RateRow({ rate, onChanged }: { rate: Row; onChanged: () => void }) {
  const [editingAmount, setEditingAmount] = useState(false);
  const [amount, setAmount] = useState(rate.amount);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmTrigger, setConfirmTrigger] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Handling fejlede');
    } finally {
      setBusy(false);
    }
  }

  async function saveAmount() {
    if (!/^-?\d+(\.\d{1,2})?$/.test(amount.trim())) {
      setErr('Beløb skal være et tal med op til 2 decimaler');
      return;
    }
    await run(() => patchRate(rate.id, { amount: amount.trim() }));
    setEditingAmount(false);
  }

  return (
    <tr className="border-b border-line-1 last:border-b-0 text-sm">
      <td className="px-2 py-1 text-ink-1">
        <div className="font-medium text-ink-0">
          {projectTypeLabel(rate.project_type)} · Rate {rate.rate_number}
        </div>
        <div className="text-meta text-ink-2 truncate max-w-[28ch]" title={rate.project_title}>
          {rate.project_title}
        </div>
      </td>
      <td className="px-2 py-1">
        {editingAmount ? (
          <span className="inline-flex items-center gap-1">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-24 rounded border border-line-2 px-1.5 py-0.5 text-sm"
              autoFocus
            />
            <button
              type="button"
              onClick={saveAmount}
              disabled={busy}
              className="rounded bg-accent px-2 py-0.5 text-xs text-white disabled:opacity-50"
            >
              OK
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingAmount(false);
                setAmount(rate.amount);
                setErr(null);
              }}
              className="rounded border border-line-2 px-2 py-0.5 text-xs text-ink-1"
            >
              Annullér
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setEditingAmount(true)}
            className="tabular-nums text-ink-0 hover:underline"
          >
            {formatDkk(rate.amount)}
          </button>
        )}
      </td>
      <td className="px-2 py-1">
        <TriggerTaskPicker rate={rate} onChanged={onChanged} />
      </td>
      <td className="px-2 py-1">
        <select
          value={rate.status}
          onChange={(e) => run(() => patchRate(rate.id, { status: e.target.value as RateStatus }))}
          disabled={busy}
          className="rounded border border-line-2 bg-paper-0 px-1 py-0.5 text-xs"
        >
          {RATE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1">
        <Pill variant={STATUS_VARIANT[rate.status]}>{STATUS_LABEL[rate.status]}</Pill>
      </td>
      <td className="px-2 py-1">
        <EmailTextSelect
          value={rate.email_custom_text}
          onChange={(next) => run(() => patchRate(rate.id, { email_custom_text: next }))}
          disabled={busy}
        />
      </td>
      <td className="px-2 py-1 text-xs text-ink-2 tabular-nums">
        {rate.triggered_at ? formatDate(rate.triggered_at) : '—'}
      </td>
      <td className="px-2 py-1 text-xs text-ink-2 tabular-nums">
        {rate.invoiced_at ? formatDate(rate.invoiced_at) : '—'}
      </td>
      <td className="px-2 py-1 text-right">
        {rate.status === 'pending' ? (
          confirmTrigger ? (
            <span className="mr-2 inline-flex items-center gap-1">
              <span className="text-xs text-status-info">Trigger nu?</span>
              <button
                type="button"
                onClick={() =>
                  run(async () => {
                    await triggerRateNow(rate.id);
                    setConfirmTrigger(false);
                  })
                }
                disabled={busy}
                className="rounded bg-status-info px-2 py-0.5 text-xs text-white disabled:opacity-50"
              >
                Ja
              </button>
              <button
                type="button"
                onClick={() => setConfirmTrigger(false)}
                className="rounded border border-line-2 px-2 py-0.5 text-xs text-ink-1"
              >
                Nej
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmTrigger(true)}
              disabled={busy}
              title="Trigger fakturaen nu — bypasser fase-udløseren."
              className="mr-2 text-xs text-ink-2 hover:text-status-info disabled:opacity-40 disabled:hover:text-ink-2"
            >
              Trigger faktura
            </button>
          )
        ) : null}
        {confirmDelete ? (
          <span className="inline-flex items-center gap-1">
            <span className="text-xs text-status-err">Slet?</span>
            <button
              type="button"
              onClick={() =>
                run(async () => {
                  await deleteRate(rate.id);
                  setConfirmDelete(false);
                })
              }
              disabled={busy}
              className="rounded bg-status-err px-2 py-0.5 text-xs text-white disabled:opacity-50"
            >
              Ja
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded border border-line-2 px-2 py-0.5 text-xs text-ink-1"
            >
              Nej
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={busy || rate.status !== 'pending'}
            title={
              rate.status !== 'pending'
                ? `Rate med status ${STATUS_LABEL[rate.status]} kan ikke slettes — brug Annullér i stedet.`
                : 'Slet rate'
            }
            className="text-xs text-ink-2 hover:text-status-err disabled:opacity-40 disabled:hover:text-ink-2"
          >
            Slet
          </button>
        )}
        {err ? <div className="text-xs text-status-err">{err}</div> : null}
      </td>
    </tr>
  );
}

function formatDate(isoOrDate: string): string {
  try {
    return new Date(isoOrDate).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return isoOrDate;
  }
}
